import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrent } from '../../hooks';
import usePost from '../../hooks/usePost';
import SelectorTree from '../SelectAccount/SelectAccount/SelectorTree';

const MwSelectorCostCenterAccount = ({
  onChangeCallback,
  withLabel = false,
  invalid = false,
  initalValue = '',
  disabled = false,
  placeholder,
  allowPrimaryAccounts = false, // New prop: default false = only secondary accounts
  showNumberField = true, // New prop: show/hide number field
  levelLimit = null, // New prop: limit tree to specific level (null = no limit)
}) => {
  const [t] = useTranslation('global');
  const { currentLangId, currentTenantId, currentCompanyId } = useCurrent();
  const [costCentersData, setCostCentersData] = useState([]);
  const hasFetched = useRef(false);
  const [accountNo, setAccountNo] = useState(initalValue);
  const [accountName, setAccountName] = useState('');

  // استدعاء API لجلب شجرة مراكز التكاليف
  const { data, postData } = usePost();

  // جلب البيانات عند تحميل المكون (مرة واحدة فقط)
  useEffect(() => {
    if (currentTenantId && currentCompanyId && !hasFetched.current) {
      hasFetched.current = true;
      postData('https://api.absat-erp.com/acc/tree-cost-center', {
        tenant_id: currentTenantId,
        company_id: currentCompanyId,
      });
    }
  }, [currentTenantId, currentCompanyId, postData]);

  // تحديث costCentersData عند وصول البيانات
  useEffect(() => {
    let rawData = [];
    if (data?.data?.treeParentCostCenter) {
      rawData = data.data.treeParentCostCenter;
    } else if (data?.data?.treeParent) {
      rawData = data.data.treeParent;
    } else if (data?.data && Array.isArray(data.data)) {
      // Fallback: if data exists as array
      rawData = data.data;
    }

    // Add level information to each node if levelLimit is provided
    if (levelLimit && rawData.length > 0) {
      console.log("Adding cost center level info with limit:", levelLimit);
      const addLevelInfo = (nodes, allowedLevel) => {
        if (!Array.isArray(nodes)) return [];
        
        return nodes.map(node => ({
          ...node,
          allowedLevel: allowedLevel, // Store the allowed level
          childs: node.childs && node.childs.length > 0
            ? addLevelInfo(node.childs, allowedLevel)
            : []
        }));
      };
      
      const dataWithLevelInfo = addLevelInfo(rawData, levelLimit);
      console.log("Cost centers with level info:", dataWithLevelInfo);
      setCostCentersData(dataWithLevelInfo);
    } else {
      console.log("No level limit for cost centers, using raw data");
      setCostCentersData(rawData);
    }
  }, [data, levelLimit]);

  // تحديث القيم عند تغيير initalValue (في وضع التعديل)
  useEffect(() => {
    setAccountNo(initalValue);
  }, [initalValue]);

  // دالة للبحث عن اسم مركز التكاليف باستخدام account_no في الشجرة
  const findCostCenterNameByNumber = (accountNo, tree) => {
    if (!accountNo || !tree || !Array.isArray(tree)) return "";
    
    console.log("Searching for cost center account_no:", accountNo, "in tree with", tree.length, "nodes");
    
    for (const node of tree) {
      // البحث في العقدة الحالية
      if (String(node.account_no) === String(accountNo)) {
        const name = currentLangId === "ar" 
          ? (node.name_ar || node.name_en || "")
          : (node.name_en || node.name_ar || "");
        console.log("Found cost center:", accountNo, "name:", name);
        return name;
      }
      
      // البحث في الأبناء (childs)
      if (node.childs && Array.isArray(node.childs)) {
        const foundName = findCostCenterNameByNumber(accountNo, node.childs);
        if (foundName) return foundName;
      }
    }
    
    console.log("Cost center not found:", accountNo);
    return "";
  };

  // عند تحميل بيانات الشجرة، حاول إيجاد الاسم إذا كان accountNo موجوداً
  useEffect(() => {
    console.log("Effect triggered - accountNo:", accountNo, "costCentersData.length:", costCentersData.length);
    if (accountNo && costCentersData.length > 0) {
      const foundName = findCostCenterNameByNumber(accountNo, costCentersData);
      console.log("Found cost center name:", foundName, "current accountName:", accountName);
      // Always update the name if we found it
      if (foundName) {
        setAccountName(foundName);
      }
    }
  }, [accountNo, costCentersData, currentLangId]);

  const handleSelect = (selected) => {
    setAccountNo(selected.account_no);
    setAccountName(selected.name);
    onChangeCallback(selected);
  };

  const handleManualNumberChange = (value) => {
    setAccountNo(value);
    // When manual number changes, we might want to update callback
    onChangeCallback({
      account_no: value,
      name_ar: "",
      name_en: "",
      raw: { account_no: value },
    });
  };

  return (
    <div>
      {withLabel && (
        <label className="text-xs text-[#261759] font-[700]">
          {t('invoice.selectCostCenterAccount')}
        </label>
      )}

      <SelectorTree
        value={accountNo}
        label={accountName}
        title={currentLangId === "ar" ? "دليل الحسابات" : "Accounts Guide"}
        headerTitle={withLabel ? "" : t('invoice.selectCostCenterAccount')}
        onSelect={handleSelect}
        onManualNumberChange={handleManualNumberChange}
        data={costCentersData.length > 0 ? costCentersData : data?.data?.treeParent || []}
        placeholderNumber={currentLangId === "ar" ? "رقم الحساب" : "Account Number"}
        placeholderName={currentLangId === "ar" ? "اسم الحساب" : "Account Name"}
        disabled={disabled}
        allowManualNumberInput={true}
        invalid={invalid}
        invalidMsg={invalid ? (currentLangId === "ar" ? "مطلوب" : "Required") : ""}
        allowedAccountTypes={allowPrimaryAccounts ? [] : ["secondary"]}
        showNumberField={showNumberField}
        withInlineSuggestions={true}
      />
    </div>
  );
};

export default MwSelectorCostCenterAccount;

